import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog'
import { merge, Observable, of, Subject, throwError, TimeoutError, zip } from 'rxjs'
import {
  catchError,
  filter,
  map,
  share,
  shareReplay,
  switchMap,
  take,
  takeWhile,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { EntityDialogComponent } from '../../dto/components/entity-dialog/entity-dialog.component'
import { EntityReference } from '../../dto'
import { HttpOptionsFactory } from '../../../models/http/http-options-factory'
import { ScriptActionDefinition, ScriptActionPayload, ScriptResponse } from '../models/script-action'
import { UrlCollection } from '../../../url-collection'
import { NotificationService } from '../../../services/notification.service'
import { DialogCleanupService } from '../../../services/dialog-cleanup.service'
import CryptUtils from '../../../auth/utils/crypt.utils'
import { AuthService } from '@dot-it/jml-lib'

export class ActionItem {
  actionDetails$: Observable<ScriptActionDefinition>
  scriptResponse$: Observable<ScriptResponse>
}

@Injectable({
  providedIn: 'root'
})
export class ActionService {
  public actionQueue$: Observable<
    Observable<{
      response: ScriptResponse
      actionDetails: ScriptActionDefinition
    }>
  >
  private actionQueueSubject: Subject<
    Observable<{
      response: ScriptResponse
      actionDetails: ScriptActionDefinition
    }>
  >

  constructor(
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private http: HttpClient,
    private dialogCleanupService: DialogCleanupService,
    private auth: AuthService
  ) {
    this.actionQueueSubject = new Subject()
    this.actionQueue$ = this.actionQueueSubject.asObservable()
  }

  // gets the final payload depending on the actionDetails
  performAction(
    actionHrid: string,
    entityRef: EntityReference,
    mainRef: EntityReference,
    callback?: (boolean) => void,
    dialogClosable?: boolean,
    suppressSuccessMessage: boolean = false
  ) {
    const payload: ScriptActionPayload = { actionHrid: actionHrid, entityReference: entityRef, mainReference: mainRef }

    // get action details from backend and add HRID to payload
    const actionDetails$ = this.getAction(payload).pipe(
      tap((actionDetails) => (payload.actionHrid = actionDetails.actionHrid)),
      shareReplay({ bufferSize: 1, refCount: true }) // minimal fix to avoid calling getAction twice when later on payloadWithParams and payloadWithoutParams create 2 branches
    )

    // following code basically is an if else wth RXJS: You either require params for script execution (payloadWithParams$) or not (payloadWoParams$)
    // IF noParams THEN emit payloadWoParams ELSE emit payloadWithParams. Afterwards execute action (the merge part) with the payload determined before.

    // for actions that do not require params just emit initial payload
    const payloadWoParams$ = actionDetails$.pipe(
      filter((actionDetails) => !actionDetails.params || actionDetails.params.length === 0),
      map((actionDetails) => {
        return { payload: payload, actionDetails: actionDetails }
      })
    )

    // used to re-enable the form inside the parameter dialog on error. Unfortunately I was unable to find a way of including it into the pipeline.
    const $enableForm = new Subject<void>()
    let hasParams = false

    // for actions with params open entity dialog and add params to payload
    const payloadWithParams$ = actionDetails$.pipe(
      filter((actionDetails) => (actionDetails.params?.length ?? 0) > 0),
      map((actionDetails) => {
        hasParams = true
        // if there is a param of type "SelectList" the dialog needs to be wider
        let width: string
        // TODO: dynamic dialog width needs to be dynamic depending on content
        !!actionDetails.params.find((param) => param.type === 'SelectList') ? (width = '1024px') : (width = '500px')

        const $onChange = new Subject()
        const dialogRef = this.dialogService.open(EntityDialogComponent, {
          data: {
            description: actionDetails.description,
            fields: actionDetails.params,
            entityRef: payload.entityReference,
            buttonText: actionDetails.buttonText,
            onChange: $onChange,
            enableForm: $enableForm.asObservable()
          },
          closable: dialogClosable,
          header: actionDetails.name,
          draggable: true,
          width: width
        })
        this.dialogCleanupService.registerDialog(dialogRef)
        return merge($onChange.asObservable(), dialogRef.onDestroy).pipe(
          map((params) => {
            payload.params = params
            return { payload: payload, actionDetails, dialogRef, enableForm: $enableForm }
          })
        )
      }),
      shareReplay(1), // Make sure the dialog is not reopened when subscribing again (E.g. when after encountering an error on execution)
      switchMap((data$) => data$),
      // if entity dialog is closed without filling params stop execution
      tap((action) => {
        if (!action.payload.params) {
          if (callback) callback(false)
        }
      }),
      takeWhile((action) => !action.actionDetails.closeParameterDialogAfterSubmit, true), // make sure to complete the observable if we immediately close the dialog
      filter((action) => !!action.payload.params)
    )

    // merge the two possibilities, execute action
    const executeAction$: Observable<{ response: ScriptResponse; actionDetails: ScriptActionDefinition }> = merge(
      payloadWoParams$,
      payloadWithParams$
    ).pipe(
      tap((action) => {
        this.notificationService.addInfoNotification('Aktion gestartet.')
        if (action['dialogRef'] && action.actionDetails.closeParameterDialogAfterSubmit) {
          action['dialogRef'].close()
        }
      }),
      switchMap((action) =>
        this.executeAction(action.payload, false).pipe(
          timeout(1000 * 60),
          map((response) => {
            return of({ response: response, actionDetails: action.actionDetails, dialogRef: action['dialogRef'] })
          })
        )
      ),
      switchMap((action) => action),
      catchError((err, caught) => {
        if (err instanceof TimeoutError) {
          this.notificationService.addErrorNotification(
            'Skript Timeout.',
            'Die Aktion konnte nicht ausgeführt werden, weil das Skript seine Ablaufzeit von 60 Sekunden überschritten hat.'
          )
        }
        if (callback) callback(false) // notify calling function that action execution has failed

        if (err instanceof TimeoutError || err instanceof HttpErrorResponse) {
          if (hasParams) {
            $enableForm.next()
            return caught // encountered error during execution.
          }
          return of(null)
        }

        return throwError(err)
      }),
      tap((action) => {
        if (!!action) {
          if (
            !suppressSuccessMessage &&
            action.response.resultType !== 'LAYOUT' &&
            action.response.resultType !== 'WIZARD' &&
            action.response.resultType !== 'TEMP_FILE'
          ) {
            // don't show success message
            this.notificationService.addSuccessNotification(
              action.response.result ? action.response.result : 'Aktion erfolgreich beendet.'
            )
          }
          if (action.dialogRef) action.dialogRef.close()
          if (callback) callback(true)
        }
      }),
      take(1)
    )

    this.actionQueueSubject.next(executeAction$)
  }

  // Action script execution
  getAction(payload: Object): Observable<ScriptActionDefinition> {
    return this.http.post<ScriptActionDefinition>(
      UrlCollection.Actions.GETACTION(),
      payload,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  executeAction(payload: Object, asnyc: boolean): Observable<ScriptResponse> {
    return this.http.post<ScriptResponse>(
      UrlCollection.Actions.EXECUTE(),
      payload,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }

  executeCodeSnippet(payload: Object) {
    payload['sign'] = CryptUtils.buildHash(payload['snippet'], this.auth.localUser.token)
    return this.http.post(
      UrlCollection.Actions.EXECUTESNIPPET(),
      payload,
      new HttpOptionsFactory().addAcceptJson().addContentTypeJson().build()
    )
  }
}
